import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList,
  Calendar,
  Clock,
  MapPin,
  BookOpen,
  AlertCircle,
  Loader2,
  CalendarX,
} from 'lucide-react';
import { fetchMyExamSchedule } from '../services/studentService';
import { fetchChildExamSchedule } from '@/features/parent/services/parentService';
import { StudentExamScheduleResponse, StudentExamEntry } from '@/types/student.types';

// Helper to format date in French
const formatDateFr = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Helper to format time range
const formatTimeRange = (startTime: string, endTime: string): string => {
  return `${startTime} - ${endTime}`;
};

// Helper to format duration
const formatDuration = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, '0')}`;
};

// Group exams by date
const groupExamsByDate = (exams: StudentExamEntry[]): Map<string, StudentExamEntry[]> => {
  const grouped = new Map<string, StudentExamEntry[]>();
  exams.forEach((exam) => {
    const existing = grouped.get(exam.date) || [];
    existing.push(exam);
    grouped.set(exam.date, existing);
  });
  return grouped;
};

interface ExamSchedulePageProps {
  /** When set (parent panel), loads this child's exams instead of the logged-in student's */
  studentId?: string;
}

const ExamSchedulePage: React.FC<ExamSchedulePageProps> = ({ studentId }) => {
  const [data, setData] = useState<StudentExamScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Default to the most recent semester; users can switch back to S1
  const [selectedSemester, setSelectedSemester] = useState<number>(2);

  const loadExamSchedule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = studentId
        ? await fetchChildExamSchedule(studentId, undefined, selectedSemester)
        : await fetchMyExamSchedule(undefined, selectedSemester);
      setData(response);
      // Update selected semester from response if available
      if (response.semester) {
        setSelectedSemester(response.semester);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  }, [selectedSemester, studentId]);

  useEffect(() => {
    loadExamSchedule();
  }, [loadExamSchedule]);

  // Grouped exams by date
  const groupedExams = data?.exams ? groupExamsByDate(data.exams) : new Map<string, StudentExamEntry[]>();

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-orange-100 rounded-lg">
            <ClipboardList className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Planning des Examens</h1>
            {data?.academicYear && (
              <p className="text-sm text-gray-500">Annee academique {data.academicYear}</p>
            )}
          </div>
        </div>
      </div>

      {/* Semester Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setSelectedSemester(1)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedSemester === 1
              ? 'bg-orange-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Semestre 1
        </button>
        <button
          onClick={() => setSelectedSemester(2)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedSemester === 2
              ? 'bg-orange-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Semestre 2
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Chargement du planning...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && data && (
        <>
          {/* Exam Period Banner */}
          {data.examPeriod && data.examPeriod.start && data.examPeriod.end && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-orange-800">
                <Calendar className="w-5 h-5" />
                <span className="font-medium">Periode des examens</span>
              </div>
              <p className="text-sm text-orange-700 mt-1">
                Du {formatDateFr(data.examPeriod.start)} au {formatDateFr(data.examPeriod.end)}
              </p>
            </div>
          )}

          {/* Empty State - No exams planned */}
          {!data.hasExams && (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarX className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {data.message || 'Les examens ne sont pas encore planifies pour ce semestre'}
              </h3>
              <p className="text-sm text-gray-500">
                Le planning sera disponible une fois que les examens seront programmes.
              </p>
            </div>
          )}

          {/* Exams List */}
          {data.hasExams && data.exams.length > 0 && (
            <div className="space-y-6">
              {Array.from(groupedExams.entries()).map(([date, exams]) => (
                <div key={date} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Date Header */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-orange-600" />
                      <span className="font-semibold text-gray-900 capitalize">
                        {formatDateFr(date)}
                      </span>
                    </div>
                  </div>

                  {/* Exams for this date */}
                  <div className="divide-y divide-gray-100">
                    {exams.map((exam) => (
                      <div key={exam.sessionId} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start gap-4">
                          {/* Time Column */}
                          <div className="flex-shrink-0 w-24">
                            <div className="flex items-center gap-1.5 text-orange-600 font-medium">
                              <Clock className="w-4 h-4" />
                              <span>{exam.startTime}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 ml-5.5">
                              {formatDuration(exam.durationHours)}
                            </p>
                          </div>

                          {/* Exam Details */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">
                              {exam.subject}
                            </h4>
                            {exam.moduleName && exam.moduleName !== exam.subject && (
                              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                                <BookOpen className="w-3.5 h-3.5" />
                                {exam.moduleName}
                              </p>
                            )}
                          </div>

                          {/* Room */}
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1.5 text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span className="font-medium">{exam.room}</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {formatTimeRange(exam.startTime, exam.endTime)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {data.hasExams && data.exams.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{data.exams.length}</span> examen(s) programme(s) pour le semestre {data.semester}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExamSchedulePage;
